'use client'

import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import { downloadFile } from '@/lib/api-client'
import type { ChatMessage as ChatMessageType, DownloadLink, ToolCall } from '@/context/AIContext'
import { ActionCard, pendingActionOf } from './ActionCard'

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
  /** Confirm the previewed action on this message (exact tool and args). */
  onConfirmAction?: (message: ChatMessageType, toolCall: ToolCall) => void
  onCancelAction?: (message: ChatMessageType) => void
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Render inline markdown (bold, code, italic) in text
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Match **bold**, `code`, and *italic*
  const regex = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const segment = match[0]
    if (segment.startsWith('**') && segment.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      // Code
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      // Italic
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Render markdown content with support for headers, lists, blockquotes
 */
export function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')
  
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {lines.map((line, i) => {
        const trimmedLine = line.trim()
        
        // Empty line - add spacing
        if (!trimmedLine) {
          return <div key={i} className="h-1" />
        }
        
        // Headers (##, ###)
        if (trimmedLine.startsWith('### ')) {
          return (
            <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(4))}
            </h4>
          )
        }
        if (trimmedLine.startsWith('## ')) {
          return (
            <h3 key={i} className="font-bold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(3))}
            </h3>
          )
        }
        
        // Blockquote
        if (trimmedLine.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
            >
              {renderInlineMarkdown(trimmedLine.slice(2))}
            </blockquote>
          )
        }
        
        // Bullet points (* or -)
        if (/^[*\-]\s/.test(trimmedLine)) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
              <span>{renderInlineMarkdown(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
            </div>
          )
        }
        
        // Numbered lists
        if (/^\d+\.\s/.test(trimmedLine)) {
          const match = trimmedLine.match(/^(\d+)\.\s(.*)/)
          if (match) {
            return (
              <div key={i} className="flex gap-2 pl-1">
                <span className="text-brand-cornflower font-medium min-w-[1.25rem]">
                  {match[1]}.
                </span>
                <span>{renderInlineMarkdown(match[2])}</span>
              </div>
            )
          }
        }
        
        // Regular paragraph
        return <p key={i}>{renderInlineMarkdown(trimmedLine)}</p>
      })}
    </div>
  )
}

/**
 * Render a Download button per generated/referenced file. Clicking fetches the
 * PDF with the authenticated session and triggers a browser download.
 */
function DownloadButtons({ downloads }: { downloads: DownloadLink[] }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [errored, setErrored] = useState<string | null>(null)

  const handleDownload = async (link: DownloadLink) => {
    setBusy(link.url)
    setErrored(null)
    try {
      const safe = link.label.replace(/[^a-z0-9]+/gi, '_').toLowerCase()
      await downloadFile(link.url, `${safe || 'download'}.pdf`)
    } catch {
      setErrored(link.url)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-col gap-1.5 pl-1 pt-1">
      {downloads.map((link) => (
        <button
          key={link.url}
          type="button"
          onClick={() => handleDownload(link)}
          disabled={busy === link.url}
          className={cn(
            'inline-flex items-center gap-2 self-start rounded-lg px-3 py-2',
            'border border-brand-cornflower/30 bg-brand-cornflower/5',
            'text-sm font-medium text-brand-navy',
            'transition-colors hover:bg-brand-cornflower/10',
            'disabled:opacity-60'
          )}
          title={`Download ${link.label}`}
        >
          {busy === link.url ? (
            <Icons.loader className="h-4 w-4 animate-spin" strokeWidth={1.5} />
          ) : (
            <Icons.download className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
          )}
          <span>{busy === link.url ? 'Preparing...' : link.label}</span>
        </button>
      ))}
      {errored && (
        <span className="text-[11px] text-red-600 pl-1">
          Download failed. Please try again.
        </span>
      )}
    </div>
  )
}

function toolErrorMessages(toolCalls: ChatMessageType['toolCalls']): string[] {
  const out: string[] = []
  for (const tc of toolCalls || []) {
    const r = tc.result
    if (r && typeof r === 'object') {
      const err = (r as Record<string, unknown>).error
      if (typeof err === 'string' && err.trim()) out.push(err.trim())
      else if (err) out.push('An action could not be completed. Nothing was changed.')
    }
  }
  return Array.from(new Set(out))
}

export function ChatMessage({ message, userName, userImage, onConfirmAction, onCancelAction }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading
  const toolErrors = toolErrorMessages(message.toolCalls)
  const pendingAction = !isUser && !isLoading ? pendingActionOf(message) : null

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-gradient-to-br from-brand-navy to-brand-purple',
            'shadow-soft'
          )}>
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex max-w-[85%] flex-col gap-1',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            'transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
            isLoading && 'animate-pulse'
          )}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-brand-muted">Working on it...</span>
            </div>
          ) : isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          ) : (
            <MarkdownContent content={message.content} />
          )}
        </div>

        {/* Timestamp */}
        <span className={cn(
          'text-[10px] text-muted-foreground/60',
          isUser ? 'pr-1' : 'pl-1'
        )}>
          {formatTime(message.timestamp)}
        </span>

        {/* Tool calls indicator */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="flex items-center gap-1.5 text-[10px] text-brand-cornflower pl-1">
            <Icons.zap className="h-3 w-3" strokeWidth={1.5} />
            <span>Used {message.toolCalls.length} tool{message.toolCalls.length > 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Tool error: show the specific (vendor-neutral) message from the backend */}
        {!isUser && toolErrors.length > 0 && (
          <div className="flex items-start gap-1.5 text-[11px] text-red-600 pl-1 max-w-full">
            <Icons.alertCircle className="h-3.5 w-3.5 mt-px flex-shrink-0" strokeWidth={1.5} />
            <span>{toolErrors[0]}</span>
          </div>
        )}

        {/* Confirm-first action card: Confirm runs the exact previewed call */}
        {pendingAction && onConfirmAction && onCancelAction && (
          <ActionCard
            toolCall={pendingAction}
            state={message.action}
            onConfirm={() => onConfirmAction(message, pendingAction)}
            onCancel={() => onCancelAction(message)}
          />
        )}

        {/* Download buttons for generated reports / referenced PDFs */}
        {!isUser && message.downloads && message.downloads.length > 0 && (
          <DownloadButtons downloads={message.downloads} />
        )}
      </div>
    </div>
  )
}
